import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import imageCompression from "browser-image-compression";
import "../styles/CreateCollectionPage.css";
import { getIdentifier } from "../utils/auth";

/* =========================
   Helpers (small + focused)
========================= */

const SUPPORTED_EXTS = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".tif", ".tiff"];
const ACCEPT = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/gif": [".gif"],
  "image/tiff": [".tif", ".tiff"],
};
const SUPPORTED_LABEL = "JPG, PNG, WebP, GIF, TIFF";

const extFromType = (type, fallbackName = "") => {
  const t = (type || "").toLowerCase();
  if (t.includes("jpeg")) return "jpg";
  if (t.includes("png")) return "png";
  if (t.includes("webp")) return "webp";
  if (t.includes("gif")) return "gif";
  if (t.includes("tiff")) return "tiff";
  const m = (fallbackName || "").match(/\.([a-z0-9]+)$/i);
  return (m?.[1] || "jpg").toLowerCase();
};

const looksLikeMobileCameraName = (name = "") => {
  const n = name.toLowerCase();
  return (
    n === "image.jpg" ||
    n === "image.jpeg" ||
    n === "photo.jpg" ||
    n === "photo.jpeg" ||
    n === "capture.jpg" ||
    n === "capture.jpeg" ||
    n === "camera.jpg" ||
    n === "camera.jpeg" ||
    /^image\d*\.(jpg|jpeg|png|heic|heif)$/i.test(name) ||
    /^photo\d*\.(jpg|jpeg|png|heic|heif)$/i.test(name)
  );
};

const renameIfMobileCamera = (file) => {
  const name = file?.name || "";
  const shouldRename = !name || looksLikeMobileCameraName(name);
  if (!shouldRename) return file;

  const ext = extFromType(file.type, name);
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  const rand = crypto?.randomUUID?.() || Math.random().toString(36).slice(2, 10);
  const newName = `mobile_${ts}_${rand}.${ext}`;

  return new File([file], newName, { type: file.type, lastModified: file.lastModified });
};

async function tiffToPngObjectURL(file) {
  const lower = (file?.name || "").toLowerCase();
  if (!(file?.type === "image/tiff" || lower.endsWith(".tif") || lower.endsWith(".tiff"))) {
    throw new Error("tiffToPngObjectURL called for non-TIFF file");
  }

  // 1) Try browser decoding first
  try {
    const bmp = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bmp, 0, 0);
    bmp.close?.();
    const blob = await new Promise((res, rej) =>
      canvas.toBlob((b) => (b ? res(b) : rej(new Error("toBlob failed"))), "image/png", 0.92)
    );
    return URL.createObjectURL(blob);
  } catch (_) {
    // fall through
  }

  // 2) Robust path
  const { fromBlob } = await import(/* webpackChunkName: "geotiff" */ "geotiff");
  const tiff = await fromBlob(file);
  const image = await tiff.getImage();
  const width = image.getWidth();
  const height = image.getHeight();
  const raster = await image.readRasters({ interleave: true });
  const samples = image.getSamplesPerPixel();

  let data;
  if (raster instanceof Uint8Array) {
    if (samples === 4) data = new Uint8ClampedArray(raster);
    else {
      data = new Uint8ClampedArray(width * height * 4);
      for (let i = 0, j = 0; i < raster.length; i += 3, j += 4) {
        data[j] = raster[i];
        data[j + 1] = raster[i + 1];
        data[j + 2] = raster[i + 2];
        data[j + 3] = 255;
      }
    }
  } else {
    let max = 0;
    for (let i = 0; i < raster.length; i++) if (raster[i] > max) max = raster[i];
    const scale = max > 255 ? 255 / max : 1;
    data = new Uint8ClampedArray(width * height * 4);
    for (let i = 0, j = 0; i < raster.length; i += samples, j += 4) {
      data[j] = raster[i] * scale;
      data[j + 1] = raster[i + 1] * scale;
      data[j + 2] = raster[i + 2] * scale;
      data[j + 3] = samples > 3 ? raster[i + 3] * scale : 255;
    }
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.putImageData(new ImageData(data, width, height), 0, 0);

  const blob = await new Promise((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error("toBlob failed"))), "image/png", 0.92)
  );
  return URL.createObjectURL(blob);
}

function CollectionDetailsForm({
  name,
  onChangeName,
  total,
  nameTaken,
  privacy,
  onTogglePrivacy,
  onSubmit,
}) {
  return (
    <div className="collection-note">
      <div className="note-row">
        <span>name:</span>
        <input
          className={`note-input ${!name.trim() ? "name-highlight" : ""}`}
          type="text"
          value={name}
          onChange={(e) => onChangeName(e.target.value)}
          placeholder="enter collection name"
          spellCheck={false}
        />
      </div>

      {!name && !nameTaken && (
        <p className="inline-hint">↳ you can always change the name + visibility later</p>
      )}
      {nameTaken && (
        <p className="inline-hint error">you already have a collection with this name</p>
      )}

      <div className="note-row">
        <span>total items:</span>
        <span className="note-value">{total}</span>
      </div>

      <div className="note-row">
        <span>visibility:</span>
        <span className="note-privacy-btn" onClick={onTogglePrivacy}>
          {privacy ? "🔐 private" : "🔓 public"}
        </span>
      </div>

      <div className="collection-button-row">
       <div className="save-button-wrap">
  <button
    className="banner-button"
    onClick={onSubmit}
    disabled={!name.trim() || nameTaken}
  >
    save your collection
  </button>

  {(!name.trim() || nameTaken) && (
    <span className="save-tooltip">
      enter a collection name to continue
    </span>
  )}
</div>

      </div>
    </div>
  );
}

/* =========================
   Page
========================= */

const CreateCollectionPage = () => {
  const navigate = useNavigate();

  const requireId = () => getIdentifier() || null;

  const [files, setFiles] = useState([]);
  const [collectionName, setCollectionName] = useState("");
  const [collectionDescription] = useState(""); // keep field for payload compatibility
  const [collectionPrivate, setCollectionPrivate] = useState(false);

  const [userCollections, setUserCollections] = useState([]);
  const [nameTaken, setNameTaken] = useState(false);

  const [showDetailsForm, setShowDetailsForm] = useState(false);

  const [uploadError, setUploadError] = useState(false);
  const [dropError, setDropError] = useState(null);

  const [unsupportedFiles, setUnsupportedFiles] = useState([]);
  const [showUnsupported, setShowUnsupported] = useState(false);

  const [compressing, setCompressing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ active: false, percent: 0, label: "" });
  const [saveError, setSaveError] = useState(null);

  // mobile drawer state (your universal nav styles)
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    const onKeyDown = (e) => e.key === "Escape" && setMobileNavOpen(false);
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
  const setVh = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  };

  setVh();
  window.addEventListener("resize", setVh);
  window.addEventListener("orientationchange", setVh);

  return () => {
    window.removeEventListener("resize", setVh);
    window.removeEventListener("orientationchange", setVh);
  };
}, []);


  // basic mobile detect (used by your CSS/layout)
  const [isMobile, setIsMobile] = useState(window.matchMedia("(max-width: 768px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // fetch user's existing collections to prevent duplicate name
  useEffect(() => {
    const fetchUserCollections = async () => {
      const identifier = requireId();
      if (!identifier) return;
      try {
        const response = await fetch(
          "https://squid-app-6s2hc.ondigitalocean.app/get-collections",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: identifier }),
          }
        );
        const data = await response.json();
        if (response.ok && Array.isArray(data.collections)) {
          const names = data.collections.map((c) => c.name.trim().toLowerCase());
          setUserCollections(names);
        }
      } catch {
        // keep silent
      }
    };
    fetchUserCollections();
  }, []);

  useEffect(() => {
    const nameTrimmed = collectionName.trim().toLowerCase();
    setNameTaken(!!nameTrimmed && userCollections.includes(nameTrimmed));
  }, [collectionName, userCollections]);

  const getExt = (name = "") => {
    const dot = name.lastIndexOf(".");
    return dot === -1 ? "" : name.slice(dot).toLowerCase();
  };

  // progress helpers
  const perFileProgressRef = useRef([]);
  const beginDropProgress = (count) => {
    perFileProgressRef.current = Array(Math.max(1, count)).fill(0);
    setProgress({ active: true, label: "uploading items…", percent: 1 });
  };
  const updateFileProgress = (idx, pct) => {
    const p = Math.max(0, Math.min(100, pct || 0));
    perFileProgressRef.current[idx] = p;
    const avg =
      perFileProgressRef.current.reduce((a, b) => a + b, 0) /
      perFileProgressRef.current.length;
    setProgress((prev) => ({ ...prev, percent: Math.round(avg) }));
  };
  const endDropProgress = () => setProgress({ active: false, percent: 0, label: "" });

  const generateId = () =>
    crypto?.randomUUID?.() || Math.random().toString(36).substring(2, 10);

  const onDrop = async (acceptedFiles, fileRejections) => {
    const rejectedByDropzone = (fileRejections || [])
      .map((r) => r.file?.name)
      .filter(Boolean);

    const extraRejected = acceptedFiles
      .filter((f) => !SUPPORTED_EXTS.includes(getExt(f.name)))
      .map((f) => f.name);

    const rejectedNames = Array.from(new Set([...rejectedByDropzone, ...extraRejected]));
    if (rejectedNames.length > 0) {
      setUnsupportedFiles(rejectedNames);
      setShowUnsupported(true);
    }

    if (acceptedFiles.length === 0 && fileRejections.length > 0) {
      setUploadError(true);
      return;
    } else {
      setUploadError(false);
    }

    setDropError(null);
    setCompressing(true);
    beginDropProgress(acceptedFiles.length);

    const enriched = await Promise.all(
      acceptedFiles.map(async (file, idx) => {
        file = renameIfMobileCamera(file);

        const lower = (file.name || "").toLowerCase();
        const isTiff =
          file.type === "image/tiff" || lower.endsWith(".tif") || lower.endsWith(".tiff");

        if (isTiff) {
          try {
            updateFileProgress(idx, 10);
            const previewUrl = await tiffToPngObjectURL(file);
            updateFileProgress(idx, 80);

            const previewBlob = await (await fetch(previewUrl)).blob();
            const previewFile = new File(
              [previewBlob],
              file.name.replace(/\.(tif|tiff)$/i, ".png"),
              { type: "image/png" }
            );

            updateFileProgress(idx, 100);
            return {
              id: generateId(),
              file, // original tiff
              previewFile, // png preview to upload
              previewUrl,
              originalPreviewUrl: previewUrl,
              // metadata defaults
              type: "image"
            };
          } catch (err) {
            setDropError("Couldn't preview this TIFF in your browser.");
            updateFileProgress(idx, 100);
            return { error: true };
          }
        }

        // standard images
        try {
          const compressed = await imageCompression(file, {
            maxWidthOrHeight: 1400,
            useWebWorker: true,
            onProgress: (p) => updateFileProgress(idx, p),
          });

          const previewUrl = URL.createObjectURL(compressed);
          const originalPreviewUrl = URL.createObjectURL(file);
          updateFileProgress(idx, 100);

          return {
            id: generateId(),
            file, // original
            previewFile: compressed, // preview asset
            previewUrl,
            originalPreviewUrl,
            type: "image",
            public: true,
            additionalImages: [],
            attachments: [],
            rotation: 0,
            zoom: 1,
            offsetX: 0,
            offsetY: 0,
          };
        } catch {
          updateFileProgress(idx, 100);
          return { error: true };
        }
      })
    );

    const valid = enriched.filter((x) => x && !x.error);
    if (valid.length > 0) {
      setFiles((prev) => [...prev, ...valid]);
      setShowDetailsForm(true); // step 1.5
    }
    endDropProgress();
    setCompressing(false);
  };

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    noKeyboard: true,
    accept: ACCEPT,
  });

  // --- Save pipeline (no optional edits in create flow) ---
  const SAVE_WEIGHTS = { presign: 0.2, upload: 0.7, metadata: 0.1 };
  const computeSavePct = (phase, frac) => {
    const { presign, upload, metadata } = SAVE_WEIGHTS;
    const base = phase === "presign" ? 0 : phase === "upload" ? presign : presign + upload;
    const span = phase === "presign" ? presign : phase === "upload" ? upload : metadata;
    return Math.round((base + span * Math.max(0, Math.min(1, frac))) * 100);
  };

 const buildChunkPayload = (chunk) => {
  return chunk.map((item) => ({
    id: item.id,
    type: "image",
    // storage urls set after presign+upload
    fileUrl: item.fileUrl,         // preview url
    originalUrl: item.originalUrl, // original url
  }));
};

  const saveMetadataChunk = async (chunk, isFirstChunk, identifier) => {
    const payload = {
      email: identifier,
      collectionName: collectionName.trim(),
      originalName: collectionName.trim(),
      collectionDescription,
      collectionPrivate,
      isEditMode: false,
      isFirstChunk,
      items: buildChunkPayload(chunk),
    };

    const response = await fetch(
      "https://squid-app-6s2hc.ondigitalocean.app/save-collection-chunk",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) throw new Error("Failed to save metadata chunk");
  };

  const fetchPresignedUrls = async (onTick, identifier, items = files) => {
    const emailId = identifier ?? requireId();
    if (!emailId) throw new Error("Missing user identifier for presign.");

    let presignTotal = 0;
    for (const it of items) {
      if (!it) continue;
      if (it.file && it.previewFile) presignTotal += 2;
    }
    let presignDone = 0;
    const tick = () => onTick?.(++presignDone, Math.max(1, presignTotal));
    if (presignTotal === 0) onTick?.(1, 1);

    const results = await Promise.all(
      items.map(async (item) => {
        if (!item) return null;

        const uploads = [];
        let originalUrl = null;
        let fileUrl = null;

        // original
        const resOriginal = await fetch(
          "https://squid-app-6s2hc.ondigitalocean.app/get-presigned-url",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: emailId,
              collectionName: collectionName.trim(),
              filename: item.file.name,
              contentType: item.file.type,
            }),
          }
        );
        const dataOriginal = await resOriginal.json();
        tick();
        uploads.push({
          type: "original",
          uploadUrl: dataOriginal.url,
          fileUrl: dataOriginal.fileUrl,
          file: item.file,
        });
        originalUrl = dataOriginal.fileUrl;

        // preview
        const resPreview = await fetch(
          "https://squid-app-6s2hc.ondigitalocean.app/get-presigned-url",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: emailId,
              collectionName: collectionName.trim(),
              filename: `preview_${item.file.name}`,
              contentType: item.previewFile.type,
            }),
          }
        );
        const dataPreview = await resPreview.json();
        tick();
        uploads.push({
          type: "preview",
          uploadUrl: dataPreview.url,
          fileUrl: dataPreview.fileUrl,
          file: item.previewFile,
        });
        fileUrl = dataPreview.fileUrl;

        return { ...item, uploads, fileUrl, originalUrl };
      })
    );

    return results.filter(Boolean);
  };

 const uploadAllFiles = async (itemsWithUrls, onTick) => {
  const MAX_CONCURRENT_UPLOADS = 2; // ↓ reduce pressure
  const RETRIES = 5;

  const allUploads = [];

  for (const item of itemsWithUrls) {
    (item.uploads || []).forEach((u) =>
      allUploads.push({ itemId: item.id, ...u })
    );
  }

  let done = 0;
  const total = allUploads.length;

  if (total === 0) {
    onTick?.(1, 1);
    return itemsWithUrls;
  }

const uploadSingle = async (upload) => {
  for (let attempt = 0; attempt <= RETRIES; attempt++) {
    try {
      const res = await fetch(upload.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": upload.file.type,
        },
        body: upload.file,
      });

      if (!res.ok) {
        throw new Error(`Upload failed: ${res.status}`);
      }

      onTick?.(++done, total);
      return;
    } catch (err) {
      console.error(
        `Retry ${attempt + 1} failed for:`,
        upload.file.name,
        err
      );

      if (attempt === RETRIES) {
        throw err;
      }

      // wait before retry
      await new Promise((r) =>
        setTimeout(r, 2000 * (attempt + 1))
      );
    }
  }
};

 const failedUploads = [];

for (let i = 0; i < allUploads.length; i += MAX_CONCURRENT_UPLOADS) {
  const chunk = allUploads.slice(i, i + MAX_CONCURRENT_UPLOADS);

  const results = await Promise.allSettled(
    chunk.map(uploadSingle)
  );

  results.forEach((result, idx) => {
    if (result.status === "rejected") {
      failedUploads.push(chunk[idx]);
    }
  });
}

if (failedUploads.length > 0) {
  console.error("Failed uploads:", failedUploads);

  throw new Error(
    `${failedUploads.length} uploads failed`
  );
}
  return itemsWithUrls;
};

  const handleSubmit = async () => {
    setSaveError(null);
    setUploading(true);
    setProgress({ active: true, percent: 1, label: "preparing…" });

    const identifier = requireId();
    if (!identifier) {
      setUploading(false);
      return;
    }

    try {
      const itemsWithUrls = await fetchPresignedUrls(
        (done, total) => {
          setProgress({
            active: true,
            label: "requesting storage…",
            percent: computeSavePct("presign", done / Math.max(1, total)),
          });
        },
        identifier,
        files
      );

      setProgress((p) => ({
        ...p,
        label: "uploading images…",
        percent: computeSavePct("presign", 1),
      }));

      await uploadAllFiles(itemsWithUrls, (done, total) => {
        setProgress({
          active: true,
          label: "uploading images…",
          percent: computeSavePct("upload", done / Math.max(1, total)),
        });
      });

      const CHUNK_SIZE = 20;
      const numChunks = Math.ceil(itemsWithUrls.length / CHUNK_SIZE) || 1;
      let chunkDone = 0;

      for (let i = 0; i < itemsWithUrls.length; i += CHUNK_SIZE) {
        const chunk = itemsWithUrls.slice(i, i + CHUNK_SIZE);
        const isFirst = i === 0;

        await saveMetadataChunk(chunk, isFirst, identifier);
        setProgress({
          active: true,
          label: "finalizing…",
          percent: computeSavePct("metadata", (++chunkDone) / numChunks),
        });
      }

      navigate("/post-save", { state: { collectionName: collectionName.trim() } });
    } catch (err) {
      console.error(err);
      setSaveError("collection not saved :( please try again or contact: shaq@thecollectorsplatform.com");
    } finally {
      setUploading(false);
      setProgress((p) => ({ ...p, active: false }));
    }
  };

  // --- Step logic ---
  const isStep1 = files.length === 0;
  const isStep15 = files.length > 0 && showDetailsForm;

  // Sidebar navigation (same as your current)
  const go = (path) => () => {
    setMobileNavOpen(false);
    navigate(path);
  };
  const goHome = go("/home-page");
  const handleContact = go("/contact");
  const handleDiscover = go("/discover");
  const handleProfile = go("/profile");
  const openLearnMore = go("/about");

  return (
    <div className={`nav-shell ${mobileNavOpen ? "nav-shell--open" : ""}`}>
    
      <div
  className={`menu-overlay ${mobileNavOpen ? "open" : ""}`}
  onClick={() => setMobileNavOpen(false)}
/>

<aside className={`slide-menu ${mobileNavOpen ? "open" : ""}`}>
  <button
    className="menu-close"
    onClick={() => setMobileNavOpen(false)}
  >
    ×
  </button>

  <nav className="menu-nav">

    <button onClick={goHome}>
      home
    </button>

     <button onClick={handleProfile}>
      profile
    </button>

   
    <div className="menu-divider" />

    <button onClick={handleDiscover}>
      browse collections
    </button>

   
    <button onClick={openLearnMore}>
      learn more
    </button>

     <button onClick={handleContact}>
      contact t.c.p.
    </button>

  </nav>
</aside>
      {/* Burger slot (mobile only) */}
      {!mobileNavOpen && (
        <header className="nav-burger-wrap" aria-label="page controls">
          <button
            type="button"
            className="nav-burger"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open menu"
          >
            ☰
          </button>
        </header>
      )}

      <div className="upload-page">
        {/* IMPORTANT: no header for step 1 + 1.5 */}
        <div className={`upload-content ${isStep1 ? "center-content" : ""}`}>
          <input {...getInputProps()} style={{ display: "none" }} />

          {/* STEP 1 */}
          {isStep1 && (
            <div className="upload-area">
              <div
  className={`dropzone ${isDragActive ? "drag-active" : ""}`}
  {...(!compressing && !uploading ? getRootProps() : {})}
>
                <div className="drop-icon">📂</div>
                <p>{isMobile ? "tap here to upload" : "drag & drop your items here or click to upload"}</p>
                <small style={{ color: uploadError || dropError ? "#ff4444" : "#00cc66" }}>
                  {dropError ||
                    (uploadError
                      ? `unsupported file. only images (${SUPPORTED_LABEL}) are allowed in this version.`
                      : "add as many items as you’d like · images only (for now)")}
                </small>
              </div>
            </div>
          )}

          {/* STEP 1.5 */}
          {isStep15 && (
            <CollectionDetailsForm
              name={collectionName}
              onChangeName={setCollectionName}
              total={files.length}
              nameTaken={nameTaken}
              privacy={collectionPrivate}
              onTogglePrivacy={() => setCollectionPrivate((p) => !p)}
              onSubmit={handleSubmit}
            />
          )}

          {showDetailsForm && showUnsupported && (
            <div className="unsupported-inline" role="status" aria-live="polite">
              <div className="unsupported-inline-row">
                <span className="unsupported-inline-title">sorry. some items weren’t added</span>
                <button
                  type="button"
                  className="unsupported-inline-close"
                  onClick={() => setShowUnsupported(false)}
                  aria-label="Dismiss message"
                >
                  ✕
                </button>
              </div>

              <p className="unsupported-inline-text">these items were rejected:</p>

              <ul className="unsupported-inline-list">
                {unsupportedFiles.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>

              <p className="unsupported-inline-footnote">currently accepting (for now): {SUPPORTED_LABEL}</p>
            </div>
          )}
        </div>

        {(compressing || uploading || progress.active) && (
          <div className="uploading-overlay">
           <div className="terminal-loading-text">

  {uploading
    ? `saving your collection… ${progress.percent}%`
    : `uploading items… ${progress.percent}%`}

  <br />
  <br />

  please wait.
  <br />
  do not close this page.

</div>
          </div>
        )}

        {saveError && (
          <div className="inline-error" role="alert" aria-live="polite">
            <span className="inline-error-text">{saveError}</span>
            <div className="inline-error-actions">
              <button className="banner-button" onClick={handleSubmit}>
                try again
              </button>
              <button className="banner-button" onClick={() => navigate("/contact")}>
                contact
              </button>
              <button className="inline-error-close" aria-label="dismiss" onClick={() => setSaveError(null)}>
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateCollectionPage;
